import { API_URLS } from "@/config/urls";
import type {
  MovePostChannelTarget,
  TopicPayload,
} from "@/features/study-group-stream/types/postChannelMove";
import { api } from "@/lib/axios";
import { handlePostApiError } from "@/lib/humhub-api/posts/errors";
import { appendTopicsToFormData } from "@/lib/humhub-api/posts/shared";
import type {
  ApiFile,
  CreatePostResponse,
  EditPostResponse,
  ReactionData,
  ReactionResponse,
  ReactionType,
  StreamContent,
  UpdatePostResponse,
} from "@/lib/humhub-api/types";

export const createPost = async (
  containerId: number,
  message: string,
  files?: File[],
  topics?: TopicPayload[],
): Promise<CreatePostResponse> => {
  try {
    const formData = new FormData();
    formData.append("data[message]", message);
    appendTopicsToFormData(formData, topics);

    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append("files[]", file);
        formData.append("fileList[]", file.name);
      });
    }

    const response = await api.post(
      `${API_URLS.POST}/container/${containerId}/with-files`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error: unknown) {
    throw handlePostApiError(error, { label: "API Error" });
  }
};

export const movePostChannel = async (
  postId: number | string,
  existingPost: StreamContent,
  target: MovePostChannelTarget,
): Promise<EditPostResponse> => {
  try {
    const { ...metadataWithoutExcluded } = existingPost.metadata;
    const nextTopics: TopicPayload[] =
      target.kind === "home"
        ? []
        : [
            {
              id: target.id,
              name: target.name,
            },
          ];

    const payload = {
      data: {
        message: existingPost.model_attributes?.message || "",
        content: {
          metadata: metadataWithoutExcluded,
          topics: nextTopics,
        },
      },
    };
    const response = await api.put(`${API_URLS.POST}/${postId}`, payload);

    return {
      success: true,
      data: response.data,
    };
  } catch (error: unknown) {
    throw handlePostApiError(error, {
      label: "Post move channel error",
      networkMessage: "Failed to move post channel",
      unexpectedMessage: "Failed to move post channel",
    });
  }
};

export const createOrDeleteReaction = async (
  postID: number | string,
  reaction: ReactionType | null,
  objectType: "post",
): Promise<ReactionResponse> => {
  try {
    const reactionData: ReactionData = {
      objectModel: objectType,
      objectId: postID,
      Reaction: { emotion: reaction },
    };

    const response = await api.post(
      `${API_URLS.REACTION}/create-or-delete`,
      reactionData,
    );

    return {
      success: true,
      currentUserReaction: response.data.currentUserReaction || null,
    };
  } catch (error: unknown) {
    throw handlePostApiError(error, { label: "Reaction API Error" });
  }
};

export const updatePost = async (
  postId: number | string,
  isPinned: boolean,
  existingContent?: StreamContent,
): Promise<UpdatePostResponse> => {
  try {
    const payload = {
      data: {
        message: existingContent?.model_attributes?.message || "",
        content: {
          metadata: {
            pinned: isPinned,
          },
        },
      },
    };

    const response = await api.put(`${API_URLS.POST}/${postId}`, payload);

    return {
      success: true,
      data: response.data,
    };
  } catch (error: unknown) {
    throw handlePostApiError(error, {
      label: "Post update error",
      networkMessage: "Failed to update post",
      unexpectedMessage: "Failed to update post",
    });
  }
};

export const editPost = async (
  postId: number | string,
  message: string,
  existingPost: StreamContent,
): Promise<EditPostResponse> => {
  try {
    const { ...metadataWithoutExcluded } = existingPost.metadata;
    const topics: TopicPayload[] = existingPost.topics.map((topic) => ({
      id: topic.id,
      name: topic.name,
    }));
    const payload = {
      data: {
        message: message,
        content: {
          metadata: metadataWithoutExcluded,
          topics,
        },
      },
    };
    const response = await api.put(`${API_URLS.POST}/${postId}`, payload);

    return {
      success: true,
      data: response.data,
    };
  } catch (error: unknown) {
    throw handlePostApiError(error, {
      label: "Post edit error",
      networkMessage: "Failed to edit post",
      unexpectedMessage: "Failed to edit post",
    });
  }
};

export const deletePost = async (postId: number | string): Promise<boolean> => {
  try {
    const response = await api.delete(`${API_URLS.POST}/${postId}`);
    return response.status === 200 || response.status === 204;
  } catch (error: unknown) {
    throw handlePostApiError(error, {
      label: "Post delete error",
      networkMessage: "Failed to delete post",
      unexpectedMessage: "Failed to delete post",
    });
  }
};

export const reportPost = async (
  postContentId: number | string,
  reason: number,
  detail: string,
): Promise<boolean> => {
  try {
    const reportPayload = {
      reason,
      detail,
    };

    const response = await api.post(
      `${API_URLS.CONTENT}/${postContentId}/report`,
      reportPayload,
    );

    return (
      response.status === 200 ||
      response.status === 201 ||
      response.status === 204
    );
  } catch (error: unknown) {
    throw handlePostApiError(error, {
      label: "Post report error",
      networkMessage: "Failed to report post",
      unexpectedMessage: "Failed to report post",
      logResponseDetails: true,
    });
  }
};

export const toggleFavorite = async (
  postContentId: number,
  isCurrentlyFavorited: boolean,
): Promise<boolean> => {
  try {
    const response = isCurrentlyFavorited
      ? await api.delete(`${API_URLS.READ_LATER}/${postContentId}`)
      : await api.post(`${API_URLS.READ_LATER}/${postContentId}`);

    return (
      response.status === 200 ||
      response.status === 201 ||
      response.status === 204
    );
  } catch (error: unknown) {
    throw handlePostApiError(error, {
      label: "Toggle favorite error",
      networkMessage: "Failed to toggle favorite status",
      unexpectedMessage: "Failed to toggle favorite status",
      logResponseDetails: true,
    });
  }
};

export const removePostFile = async (
  postId: number,
  fileId: number,
): Promise<void> => {
  try {
    await api.delete(`${API_URLS.POST}/${postId}/remove-file/${fileId}`);
  } catch (error: unknown) {
    throw handlePostApiError(error, {
      label: "Post file remove error",
      networkMessage: "Failed to remove file from post",
      unexpectedMessage: "Failed to remove file from post",
    });
  }
};

export const replacePostFile = async (
  postId: number,
  fileId: number,
  newFile: File,
): Promise<ApiFile> => {
  try {
    const formData = new FormData();
    formData.append("files", newFile);
    const response = await api.post(
      `${API_URLS.POST}/${postId}/replace-file?fileId=${fileId}`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data;
  } catch (error: unknown) {
    throw handlePostApiError(error, {
      label: "Post file replace error",
      networkMessage: "Failed to replace file in post",
      unexpectedMessage: "Failed to replace file in post",
    });
  }
};

export const uploadFilesToPost = async (
  postId: number,
  files: File[],
): Promise<ApiFile[]> => {
  try {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files[]", file);
    });
    const response = await api.post(
      `${API_URLS.POST}/${postId}/upload-files`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data;
  } catch (error: unknown) {
    throw handlePostApiError(error, {
      label: "Post file upload error",
      networkMessage: "Failed to upload files to post",
      unexpectedMessage: "Failed to upload files to post",
    });
  }
};
